import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";
import { withSelect } from "@wordpress/data";
import { __experimentalGetSettings } from "@wordpress/date";
import { Fragment, useMemo } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React, { useEffect } from "react";
import { useBlockStyle } from "../../hooks";
import "../assets/sass/blocks/category/style.scss";
import InspectorControls from "./components/InspectorControls";
import { __ } from "@wordpress/i18n";

interface CategoryObject {
	id: number;
	name: string;
	slug: string;
	isCurrentPost?: boolean;
	displayName?: string;
}

interface Props extends EditProps<any> {
	categories: Array<CategoryObject>;
	posts: Array<any>;
	currentPost: any;
	currentPostCategories: number[];
	currentPostCategoryObjects: Array<CategoryObject>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		className,
		attributes: { category, hideOnDesktop },
		setAttributes,
		categories,
		currentPost,
		currentPostCategories,
		currentPostCategoryObjects,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();

	const { Style } = useBlockStyle({
		blockName: "category",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	useEffect(() => {
		if (
			!category &&
			currentPostCategories &&
			currentPostCategories.length > 0 &&
			categories &&
			categories.length > 0
		) {
			const firstPostCategory = currentPostCategories.find((catId) =>
				categories.some((cat) => cat.id === catId)
			);

			if (firstPostCategory) {
				setAttributes({ category: firstPostCategory });
			}
		}
	}, [category, currentPostCategories, categories, setAttributes]);

	const isCurrentPostCategory = useMemo(() => {
		return (
			currentPostCategories &&
			category &&
			currentPostCategories.includes(parseInt(category.toString()))
		);
	}, [currentPostCategories, category]);

	const enhancedCategories = useMemo(() => {
		if (!categories || !currentPostCategories) return categories;

		return categories.map((cat) => ({
			...cat,
			isCurrentPost: currentPostCategories.includes(cat.id),
			displayName: currentPostCategories.includes(cat.id)
				? `${cat.name} (Current Post)`
				: cat.name,
		}));
	}, [categories, currentPostCategories]);

	const allCurrentPostCategories = useMemo(() => {
		if (!categories || !currentPostCategories) return [];
		return categories.filter((cat) =>
			currentPostCategories.includes(cat.id)
		);
	}, [categories, currentPostCategories]);

	const classNames = classnames(
		`mzb-category mzb-category-${clientId}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop"
	);

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={enhancedCategories}
				currentPostCategories={currentPostCategories}
				currentPostCategoryObjects={currentPostCategoryObjects}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				{currentPost && (
					<>
						{allCurrentPostCategories.length > 0 && (
							<div className="mzb-category-list">
								{allCurrentPostCategories.map((cat, index) => (
									<span
										key={cat.id}
										className="mzb-category-item"
									>
										{cat.name}
									</span>
								))}
							</div>
						)}
						{allCurrentPostCategories.length === 0 && (
							<p>{__("Category", "magazine-blocks")}</p>
						)}
					</>
				)}
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: { category },
	} = props;

	const post = getCurrentPost();

	const allCategories =
		getEntityRecords("taxonomy", "category", { per_page: -1 }) || [];

	const postCategoryIds = post?.categories || [];

	const currentPostCategoryObjects = postCategoryIds
		.map((catId: number) => {
			const foundCategory = allCategories.find(
				(cat: any) => cat.id === catId
			);
			return foundCategory;
		})
		.filter(Boolean);

	return {
		categories: allCategories,
		currentPost: post,
		currentPostCategories: postCategoryIds,
		currentPostCategoryObjects: currentPostCategoryObjects,
	};
})(Edit);
